import { useState } from 'react';
import { Clock } from 'lucide-react';
import type { DigestPref } from '@awwyeah/shared';
import type * as Y from 'yjs';

/**
 * "Digest arrives at 7:00 · change" (ROADMAP §2 pref placement — Settings has no extension
 * point yet, so the control lives at the top of the view it affects instead). Writes
 * straight to the `household` doc's `digestPrefs` map — no REST, matching every other
 * collaborative write in the app (ROADMAP §1.3).
 */
export function DigestPrefControl({
  memberId,
  currentTime,
  map,
}: {
  memberId: string;
  currentTime: string;
  map: Y.Map<DigestPref>;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentTime);

  const save = (): void => {
    if (!draft) return;
    map.set(memberId, { time: draft, updatedAt: new Date().toISOString() });
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="digest-pref-control">
        <Clock size={14} className="text-fg-3" aria-hidden="true" />
        <label className="sr-only" htmlFor="digest-pref-time">
          Digest arrival time
        </label>
        <input
          id="digest-pref-time"
          type="time"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="h-11 rounded border border-line bg-surface px-2 text-sm text-fg"
          data-testid="digest-pref-time-input"
        />
        <button
          type="button"
          onClick={save}
          data-testid="digest-pref-save"
          className="flex h-11 min-w-[44px] items-center justify-center rounded-md bg-inverse px-3 text-sm font-semibold text-inverse-fg"
        >
          Save
        </button>
        <button
          type="button"
          onClick={() => {
            setDraft(currentTime);
            setEditing(false);
          }}
          className="flex h-11 min-w-[44px] items-center justify-center px-2 text-sm text-fg-3"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 text-sm text-fg-3" data-testid="digest-pref-control">
      <Clock size={14} aria-hidden="true" />
      <span>Digest arrives at {formatTimeLabel(currentTime)}</span>
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid="digest-pref-change"
        className="font-semibold text-member-faye"
      >
        · change
      </button>
    </div>
  );
}

/** "7:00am" / "6:30pm" — a light local formatter; this file never touches server time. */
function formatTimeLabel(hhmm: string): string {
  const [hourStr, minuteStr] = hhmm.split(':');
  const hour = Number(hourStr ?? 0);
  const minute = Number(minuteStr ?? 0);
  const suffix = hour < 12 ? 'am' : 'pm';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return minute === 0
    ? `${hour12}${suffix}`
    : `${hour12}:${String(minute).padStart(2, '0')}${suffix}`;
}
